import { useEffect, useId, useLayoutEffect, useRef, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, X } from 'lucide-react';
import { cn } from '../../utils/utils';

export interface SheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Which edge the panel slides in from. */
  side?: 'bottom' | 'right';
  /**
   * How much of the viewport a bottom sheet takes. `full` covers it (a task
   * like managing sources); `tall` leaves a strip of the page showing above
   * it, so the reader keeps their place (the lesson chat). Ignored for
   * `side='right'`, which is always full height. `auto` fits its content,
   * up to the same cap as `tall` (a short form or a pick-one list).
   */
  size?: 'auto' | 'tall' | 'full';
  /** Visible heading. Without one, pass `ariaLabel`. */
  title?: string;
  ariaLabel?: string;
  /**
   * Keep the content mounted while closed - hidden, `inert` and off-screen.
   * For a panel with live state the page relies on even when it is not
   * showing: the lesson chat keeps its conversation, and must still be
   * subscribed when "Explain" asks it to fill its input.
   */
  keepMounted?: boolean;
  /** The built-in close bar. Off when the content draws its own header. */
  showHeader?: boolean;
  /**
   * A back arrow before the title, for a sheet with steps (pick a kind of
   * source, then fill it in). The close button stays: it leaves the sheet.
   */
  onBack?: () => void;
  className?: string;
  /**
   * Classes for the full-screen layer (scrim and panel), as `Modal` takes
   * them. For a sheet that must sit ABOVE other open sheets whatever order
   * they mounted in - an app-wide interruption such as the usage-limit
   * sheet, raised over the lesson's chat sheet (`z-[60]`).
   */
  overlayClassName?: string;
}

const PANEL_CLOSED = {
  bottom: 'translate-y-full',
  right: 'translate-x-full',
} as const;

const PANEL_SHAPE = {
  bottom: 'inset-x-0 bottom-0 rounded-t-2xl border-t',
  right: 'inset-y-0 right-0 w-full max-w-md border-l',
} as const;

const BOTTOM_HEIGHT = {
  auto: 'max-h-[calc(100dvh-3rem)]',
  // `dvh`, not `vh`: mobile Safari's `vh` includes the area behind its
  // toolbar, so a 100vh sheet hides its own bottom edge - the chat input.
  tall: 'h-[calc(100dvh-3rem)]',
  // Clears the status bar if the page ever opts into `viewport-fit=cover`.
  full: 'h-[100dvh] rounded-t-none border-t-0 pt-[env(safe-area-inset-top)]',
} as const;

/**
 * A panel that slides in from the bottom (phones) or the right (tablets).
 *
 * The phone counterpart of `Modal`: same portal, Escape, scroll lock and
 * focus hand-off, but anchored to an edge and able to stay mounted while
 * closed (`keepMounted`). Portalled for the reason `Modal` is - a `fixed`
 * element inside a `backdrop-filter` ancestor is fixed to that ancestor.
 */
export function Sheet({
  isOpen,
  onClose,
  children,
  side = 'bottom',
  size = 'tall',
  title,
  ariaLabel,
  keepMounted = false,
  showHeader = true,
  onBack,
  className,
  overlayClassName,
}: SheetProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  // A partial-height bottom sheet has a handle and follows a downward swipe;
  // a full-screen one and a side drawer close with their button only.
  const draggable = side === 'bottom' && size !== 'full';
  const swipe = useSwipeToDismiss(panelRef, onClose, draggable && isOpen);

  useSheetEffects(isOpen, onClose, panelRef);

  if (!isOpen && !keepMounted) return null;
  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-50',
        // Closed but mounted: no box takes a tap, nothing is announced.
        !isOpen && 'pointer-events-none',
        overlayClassName
      )}
      aria-hidden={isOpen ? undefined : true}
      data-state={isOpen ? 'open' : 'closed'}
    >
      <div
        className={cn(
          'absolute inset-0 bg-black/40 transition-opacity duration-300 motion-reduce:transition-none',
          isOpen ? 'opacity-100' : 'opacity-0'
        )}
        onClick={onClose}
        aria-hidden='true'
      />
      <div
        ref={panelRef}
        role='dialog'
        tabIndex={-1}
        aria-modal={isOpen ? 'true' : undefined}
        aria-labelledby={title ? titleId : undefined}
        aria-label={!title ? ariaLabel : undefined}
        className={cn(
          'border-border bg-card absolute flex flex-col overflow-hidden shadow-2xl outline-none',
          PANEL_SHAPE[side],
          side === 'bottom' && BOTTOM_HEIGHT[size],
          side === 'right' && 'h-[100dvh]',
          // Closed: off-screen AND `invisible`, or it would stay in the tab
          // order. Visibility flips only once the 300ms slide-out is done.
          isOpen
            ? 'visible translate-x-0 translate-y-0 [transition:transform_300ms_ease-out]'
            : cn(
                PANEL_CLOSED[side],
                'invisible [transition:transform_300ms_ease-out,visibility_0s_linear_300ms]'
              ),
          'motion-reduce:[transition:none]',
          className
        )}
      >
        {/* The drag zone: the handle and the header. Not the content, which
            scrolls - a swipe there must scroll, not close. */}
        <div className='flex-shrink-0 touch-none' {...swipe}>
          {draggable && (
            <div className='flex justify-center pt-2 pb-1' aria-hidden='true'>
              <div className='bg-muted-foreground/30 h-1 w-10 rounded-full' />
            </div>
          )}
          {showHeader && (
            <div className='border-border flex items-center gap-1 border-b px-4 py-2'>
              {onBack && (
                <button
                  type='button'
                  aria-label='Back'
                  onClick={onBack}
                  className='text-muted-foreground hover:bg-muted hover:text-foreground -ml-2 rounded-lg p-3 transition-colors'
                >
                  <ArrowLeft className='h-5 w-5' />
                </button>
              )}
              <h2
                id={titleId}
                className='text-foreground min-w-0 flex-1 truncate text-base font-semibold'
              >
                {title}
              </h2>
              <button
                type='button'
                aria-label='Close'
                onClick={onClose}
                className='text-muted-foreground hover:bg-muted hover:text-foreground -mr-2 rounded-lg p-3 transition-colors'
              >
                <X className='h-5 w-5' />
              </button>
            </div>
          )}
        </div>
        <div className='flex min-h-0 flex-1 flex-col pb-[env(safe-area-inset-bottom)]'>
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}

/** How far down (px) a released swipe must have travelled to close. */
const DISMISS_DISTANCE = 80;

/**
 * Swipe down on the handle or header to close - the third way out a bottom
 * sheet is expected to have, after the close button and the backdrop. The
 * panel follows the finger, then either closes or springs back.
 */
function useSwipeToDismiss(
  panelRef: React.MutableRefObject<HTMLDivElement | null>,
  onClose: () => void,
  enabled: boolean
) {
  const start = useRef<number | null>(null);
  const setOffset = (y: number) => {
    const panel = panelRef.current;
    if (!panel) return;
    panel.style.transform = y > 0 ? `translateY(${y}px)` : '';
    panel.style.transition = y > 0 ? 'none' : '';
  };
  if (!enabled) return {};
  return {
    onPointerDown: (e: React.PointerEvent) => {
      // Not from a button in the header: a tap on Close or Back is a tap.
      if ((e.target as HTMLElement).closest('button')) return;
      start.current = e.clientY;
      (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    },
    onPointerMove: (e: React.PointerEvent) => {
      if (start.current !== null) setOffset(e.clientY - start.current);
    },
    onPointerUp: (e: React.PointerEvent) => {
      if (start.current === null) return;
      const travelled = e.clientY - start.current;
      start.current = null;
      setOffset(0);
      if (travelled > DISMISS_DISTANCE) onClose();
    },
    onPointerCancel: () => {
      start.current = null;
      setOffset(0);
    },
  };
}

/**
 * Escape, scroll lock, `inert` while closed, and focus in on open / back
 * out on close. One hook so `Sheet` itself stays a render function.
 */
function useSheetEffects(
  isOpen: boolean,
  onClose: () => void,
  panelRef: React.MutableRefObject<HTMLDivElement | null>
) {
  // React 18 has no `inert` prop; set the attribute directly. A closed,
  // still-mounted sheet must be unreachable by Tab and by screen readers.
  // A layout effect, so it lands before the chat's rAF focus on open.
  useLayoutEffect(() => {
    panelRef.current?.toggleAttribute('inert', !isOpen);
  });

  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Sheets stack (versions over sources, attach over sources). Every
      // open one listens here, so only the TOPMOST - the last open modal
      // dialog in the document - answers, or one Escape closed them all.
      const open = document.querySelectorAll(
        '[role="dialog"][aria-modal="true"]'
      );
      if (open.length > 0 && open[open.length - 1] !== panelRef.current) return;
      onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const raf = window.requestAnimationFrame(() => {
      // Leave focus alone if something inside already took it (the chat
      // focuses its input when "Explain" opens the sheet).
      if (panel && !panel.contains(document.activeElement)) panel.focus();
    });
    return () => {
      window.cancelAnimationFrame(raf);
      const active = document.activeElement;
      const stillInside =
        active === null ||
        active === document.body ||
        (panel?.contains(active) ?? false);
      if (previouslyFocused && stillInside) previouslyFocused.focus?.();
    };
  }, [isOpen, panelRef]);
}
